import type {
  AiCollapseJudgment,
  EmergencyInspection,
  ResidentialDamageInspection
} from '@/types/api/mapViewData/mapViewDataType'

/** 表示区分の閾値（最小値） */
export const ZOOM_THRESHOLD = {
  MAX_LAYER: 19, // レイヤー表示最大
  MAX: 18, // 最大
  MAX_HAZARD_LAYER: 17, // ハザードマップ用の表示最大
  NARROW: 16, // 狭域：16以上
  MIDDLE: 14, // 中域：14以上、16未満
  WIDE: 0 // 広域：14未満
} as const

/** 表示区分 */
export enum MapViewport {
  NARROW = 'narrow', // 狭域表示
  MIDDLE = 'middle', // 中域表示
  WIDE = 'wide' // 広域表示
}

/** グリッドサイズ */
export const GRID_SIZE = {
  NARROW: 100, // 狭域
  WIDE: 500 // 広域
} as const

/** 住家被害認定調査の被害度リスト */
export const RESIDENTIAL_DAMAGE_ITEMS: { key: keyof ResidentialDamageInspection; label: string }[] =
  [
    { key: 'partiallyDamaged', label: '一部損壊' },
    { key: 'semiPartiallyDestroyed', label: '準半壊' },
    { key: 'partiallyDestroyed', label: '半壊' },
    { key: 'halfTotallyDestroyed', label: '中規模半壊' },
    { key: 'almostTotallyDestroyed', label: '大規模半壊' },
    { key: 'totallyDestroyed', label: '全壊' },
    { key: 'floodingUnderFloor', label: '床下浸水' },
    { key: 'floodingAboveFloor', label: '床上浸水' }
  ]

/** AI倒壊判定の被害度リスト */
export const AI_COLLAPSE_JUDGMENT_ITEMS: { key: keyof AiCollapseJudgment; label: string }[] = [
  { key: 'notTotallyDestroyed', label: '非全壊' },
  { key: 'totalCollapse', label: '全壊' }
]

/** 応急危険度判定調査の被害度リスト */
export const EMERGENCY_INSPECTION_ITEMS: { key: keyof EmergencyInspection; label: string }[] = [
  { key: 'inspected', label: '調査済み' },
  { key: 'warning', label: '要注意' },
  { key: 'danger', label: '危険' }
]

/** 各ステータスアイコンのマップ */
export const STATUS_ICON_MAP: Record<string, string> = {
  '00': 'icon_default', // 何も結果がない場合

  '01': 'icon_resi_n_uninvestigated', // 未判定（住家被害認定調査）
  '02': 'icon_eme_uninvestigated', // 未判定（応急危険度判定調査）
  '03': 'icon_ai_uninvestigated', // 未判定（AI倒壊判定）

  '11': 'icon_ai_notTotallyDestroyed', // 非全壊（倒壊判定AI）
  '12': 'icon_ai_totalCollapse', // 全壊（倒壊判定AI）

  '21': 'icon_eme_inspected', // 調査済み
  '22': 'icon_eme_warning', // 要注意
  '23': 'icon_eme_danger', // 危険

  '30': 'icon_default', // 無被害
  '31': 'icon_resi_n_partiallyDamaged', // 一部損壊
  '32': 'icon_resi_n_semiPartiallyDestroyed', // 準半壊
  '33': 'icon_resi_n_partiallyDestroyed', // 半壊
  '34': 'icon_resi_n_halfTotallyDestroyed', // 中規模半壊
  '35': 'icon_resi_n_almostTotallyDestroyed', // 大規模半壊
  '36': 'icon_resi_n_totallyDestroyed', // 全壊
  '37': 'icon_default', // 判定不可

  '40': 'icon_default', // 無被害
  '41': 'icon_resi_u_partiallyDamaged', // 一部損壊（床下）
  '42': 'icon_resi_u_semiPartiallyDestroyed', // 準半壊（床下）
  '43': 'icon_resi_u_partiallyDestroyed', // 半壊（床下）
  '44': 'icon_resi_u_halfTotallyDestroyed', // 中規模半壊（床下）
  '45': 'icon_resi_u_almostTotallyDestroyed', // 大規模半壊（床下）
  '46': 'icon_resi_u_totallyDestroyed', // 全壊（床下）
  '47': 'icon_default', // 判定不可

  '50': 'icon_default', // 無被害
  '51': 'icon_resi_a_partiallyDamaged', // 一部損壊（床上）
  '52': 'icon_resi_a_semiPartiallyDestroyed', // 準半壊（床上）
  '53': 'icon_resi_a_partiallyDestroyed', // 半壊（床上）
  '54': 'icon_resi_a_halfTotallyDestroyed', // 中規模半壊（床上）
  '55': 'icon_resi_a_almostTotallyDestroyed', // 大規模半壊（床上）
  '56': 'icon_resi_a_totallyDestroyed', // 全壊（床上）
  '57': 'icon_default', // 判定不可

  '61': 'icon_resi_floodingUnderFloor', // 調査済み（床下）
  '62': 'icon_resi_floodingUnderFloor', // 要注意（床下）
  '63': 'icon_resi_floodingUnderFloor', // 危険（床下）

  '71': 'icon_resi_floodingAboveFloor', // 調査済み（床上）
  '72': 'icon_resi_floodingAboveFloor', // 要注意（床上）
  '73': 'icon_resi_floodingAboveFloor', // 危険（床上）

  '81': 'icon_resi_floodingUnderFloor', // 非全壊（床下）
  '82': 'icon_resi_floodingUnderFloor', // 全壊（床下）

  '91': 'icon_resi_floodingAboveFloor', // 非全壊（床上）
  '92': 'icon_resi_floodingAboveFloor', // 全壊（床上）

  '101': 'icon_resi_floodingUnderFloor', // 床下浸水
  '102': 'icon_resi_floodingAboveFloor' // 床上浸水
} as const
